import {
  BY_HOURS_LABEL,
  NOT_CREDITED_LABEL,
  SPLIT_EVENLY_LABEL,
  rangeLabel,
  type ComparedCents,
  type ReportResult,
  type ReportRow,
  type RevenuePage,
} from '@servwell/shared';
import { BASIS_WORDS } from './basisWords';
import {
  REVENUE_SECTION,
  byPersonReasons,
  byPersonShown,
  chartWindowWords,
  monthWords,
  showsNotCredited,
} from './onScreen';

/**
 * The Revenue page as one file (ops insights Phase 2, ticket 08): the answer on screen as ONE
 * `ReportResult`, for the one CSV writer every report uses (`toCsv`). Every number is the
 * server's; every word, and which rows show, is the page's (`onScreen.ts`, `BASIS_WORDS`).
 *
 * It holds the ticket's seven columns and its list of rows, not every word on screen. Left out:
 * the averages, the shares, the changes and the rule totals; Not credited's two reasons; the
 * fourth figure (the other basis's own headline); a year earlier's job count, which no column
 * holds; each person's mix of service types; the caveats; and the drill.
 */

/** A row's numbers: any left out is an empty cell. */
type Cells = Partial<
  Record<'jobs' | 'amount' | 'yearEarlier' | 'byHours' | 'splitEvenly', number | null>
>;

const row = (section: string, name: string, cells: Cells = {}): ReportRow => ({
  section,
  name,
  jobs: null,
  amount: null,
  yearEarlier: null,
  byHours: null,
  splitEvenly: null,
  ...cells,
});

const compared = (x: ComparedCents): Cells => ({
  amount: x.cents,
  yearEarlier: x.yearEarlierCents,
});

const ABOUT = 'About';

/** The days an answer is of, in words: "Aug 1–10, 2026". */
const periodWords = (page: RevenuePage) => rangeLabel(page.range.from, page.range.to);

export function revenueCsv(page: RevenuePage): ReportResult {
  const words = BASIS_WORDS[page.basis];
  const days = periodWords(page);
  const yearEarlierDays = rangeLabel(page.yearEarlier.from, page.yearEarlier.to);
  return {
    columns: [
      { key: 'section', label: 'Section', kind: 'text' },
      { key: 'name', label: 'Name', kind: 'text' },
      { key: 'jobs', label: words.jobsColumn, kind: 'number' },
      { key: 'amount', label: words.label, kind: 'money' },
      { key: 'yearEarlier', label: 'Year earlier', kind: 'money' },
      { key: 'byHours', label: BY_HOURS_LABEL, kind: 'money' },
      { key: 'splitEvenly', label: SPLIT_EVENLY_LABEL, kind: 'money' },
    ],
    rows: [
      row(ABOUT, `${words.label}, ${days}, compared with ${yearEarlierDays}`),
      row(ABOUT, words.definition),
      row(ABOUT, chartWindowWords(page.range.to)),
      row(words.label, days, {
        jobs: page.kpis.jobs.count,
        ...compared(page.kpis.total),
      }),
      ...page.trend.map((m) =>
        row(REVENUE_SECTION.byMonth(words.label), monthWords(m), compared(m)),
      ),
      ...page.byServiceType.map((r) =>
        row(REVENUE_SECTION.byServiceType, r.label, { jobs: r.jobs, ...compared(r) }),
      ),
      ...personRows(page),
    ],
    figures: [],
    caveats: page.caveats,
  };
}

/** By person as the page draws it: the people and Not credited, or the server's reason. */
function personRows(page: RevenuePage): ReportRow[] {
  const section = REVENUE_SECTION.byPerson;
  if (!byPersonShown(page)) {
    return byPersonReasons(page.withheld).map((w) => row(section, w.reason));
  }
  const { rows, notCredited } = page.byPerson;
  return [
    ...rows.map((r) =>
      row(section, r.name, {
        jobs: r.jobs,
        ...compared(r),
        byHours: r.byHoursCents,
        splitEvenly: r.splitEvenlyCents,
      }),
    ),
    ...(showsNotCredited(notCredited)
      ? [row(section, NOT_CREDITED_LABEL, compared(notCredited))]
      : []),
  ];
}

/** The file's title, which `downloadReportCsv` names it by: its basis and its period. */
export function revenueCsvTitle(page: RevenuePage): string {
  return `Revenue ${BASIS_WORDS[page.basis].label} ${periodWords(page)}`;
}
